import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api, type Desk } from './api'
import { FilterHeader, type Sort } from './components/Grid'

/**
 * Which desk a fund or group is on, for the screens that list them — decision 89.
 *
 * Somebody across the whole firm reads funds from every desk on Approvals, Sign-off,
 * Lookup, Compare, Search and the verdict screens, and needs to know whose each one
 * is. Somebody limited to desks sees only their own, so naming the desk on every row
 * would say the same thing everywhere; for them the account menu says where they work,
 * and the screens stay as they were.
 *
 * Read once, from the funds and groups the reader can see, rather than added to every
 * payload: a fund's desk is one fact, and one place to read it keeps every screen
 * saying the same thing.
 */
export interface DeskDirectory {
  /** Reaches the whole firm, so the screens name each fund's desk. */
  firmWide: boolean
  /** The desks this person works on (their own), or every desk for the whole firm. */
  desks: Desk[]
  /** A fund's desk code, by its id or its code. */
  fund: (idOrCode?: string | null) => string | undefined
  /** A group's desk code, or "Whole firm" for the firm's own groups. */
  group: (idOrCode?: string | null) => string | undefined
}

const NONE: DeskDirectory = { firmWide: false, desks: [], fund: () => undefined, group: () => undefined }

export const DeskContext = createContext<DeskDirectory>(NONE)

export function useDesks(): DeskDirectory {
  return useContext(DeskContext)
}

export function DeskProvider({ firmWide, children }: { firmWide: boolean; children: ReactNode }) {
  const [directory, setDirectory] = useState<DeskDirectory>({ ...NONE, firmWide })

  useEffect(() => {
    let live = true
    Promise.all([
      api.desks().catch(() => [] as Desk[]),
      firmWide ? api.portfolios().catch(() => []) : Promise.resolve([]),
      firmWide ? api.scopes().catch(() => []) : Promise.resolve([]),
    ]).then(([desks, funds, groups]) => {
      if (!live) return
      const byFund = new Map<string, string>()
      for (const f of funds) {
        if (!f.desk_code) continue
        byFund.set(f.id, f.desk_code)
        byFund.set(f.code.toLowerCase(), f.desk_code)
      }
      const byGroup = new Map<string, string>()
      for (const g of groups) {
        const owner = g.desk_code ?? 'Whole firm'
        byGroup.set(g.id, owner)
        byGroup.set(g.code.toLowerCase(), owner)
      }
      setDirectory({
        firmWide,
        desks,
        fund: (key) => (key ? (byFund.get(key) ?? byFund.get(key.toLowerCase())) : undefined),
        group: (key) => (key ? (byGroup.get(key) ?? byGroup.get(key.toLowerCase())) : undefined),
      })
    })
    return () => {
      live = false
    }
  }, [firmWide])

  return <DeskContext.Provider value={directory}>{children}</DeskContext.Provider>
}

/**
 * The Desk column's header: first in the table, sorting and filtering like the rest
 * (decision 91). Drawn only for somebody across the whole firm; everybody else's
 * tables are as they were, since every row would carry their own desk.
 */
export function DeskHeader({
  sort,
  onSort,
  value,
  onChange,
}: {
  sort: Sort | null
  onSort: (key: string) => void
  value: string
  onChange: (desk: string) => void
}) {
  const { firmWide, desks } = useDesks()
  if (!firmWide) return null
  return (
    <FilterHeader
      label="Desk"
      sortKey="desk"
      sort={sort}
      onSort={onSort}
      value={value}
      onChange={onChange}
      all="Every desk"
      testId="filter-desk"
      options={desks.map((d) => ({ value: d.code, label: d.code }))}
    />
  )
}

/** The Desk column's cell, for a fund (by id or code) or a group. */
export function DeskCell({ fund, group }: { fund?: string | null; group?: string | null }) {
  const { firmWide, fund: ofFund, group: ofGroup } = useDesks()
  if (!firmWide) return null
  const desk = fund ? ofFund(fund) : ofGroup(group)
  return (
    <td className="desk-cell" data-testid="desk-cell">
      {desk ?? ''}
    </td>
  )
}

/** One more column for the whole firm, for a row that spans the table. */
export function useDeskColumns(): number {
  return useDesks().firmWide ? 1 : 0
}
